/**
 * Activity Logs — the Super Admin's whole-platform activity history.
 *
 * One destination replacing two: the old Security page (auth statistics + a 50-row feed) and
 * the old Audit Log page (platform rows only). Both remain mounted during the deprecation
 * window; their routes redirect here.
 *
 * TENANT CONFIDENTIALITY: rows sourced from a chambers carry metadata only — no case name, no
 * amount, no filename, no subject. That is enforced server-side by the SELECT shape, not here;
 * this page simply never receives the fields. Where a target exists but its name is withheld,
 * the UI says so explicitly rather than rendering an empty cell, because "withheld" and
 * "absent" mean different things to someone investigating.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CircleNotch, DownloadSimple, FunnelSimple, X, Warning,
  ShieldCheck, Lock, Tray, CaretRight, CaretLeft,
} from '@phosphor-icons/react';
import {
  fetchActivity, fetchActivitySummary, downloadActivityCsv,
  ACTIVITY_CATEGORIES, ACTIVITY_CATEGORY_LABEL, ACTIVITY_SOURCES, ACTIVITY_SOURCE_LABEL,
  ACTOR_KINDS, ACTOR_KIND_LABEL,
  type ActivityRecord, type ActivityFilters, type ActivityCategory,
  type ActivitySource, type ActivityTone, type ActorKind,
} from '@/lib/platformActivityApi';
import { getPlatformApiErrorMessage } from '@/lib/platformApi';
import { TenantSelect } from '@/components/platform/TenantSelect';

// ── Presentation ────────────────────────────────────────────────────────────────────────

const TONE_DOT: Record<ActivityTone, string> = {
  created: 'bg-plat-success-text',
  changed: 'bg-plat-info-text',
  removed: 'bg-plat-danger-text',
  viewed:  'bg-plat-neutral-text',
  money:   'bg-plat-purple-text',
  // Indigo, not info: `changed` already owns the info hue, and the dot is the only thing that
  // distinguishes these two event kinds at a glance. It also matches the auth badge in
  // `lib/platformAuditActions.ts`, so the same event reads the same way on both pages.
  auth:    'bg-plat-indigo-text',
  alert:   'bg-plat-warning-text',
};

const SOURCE_CHIP: Record<ActivitySource, string> = {
  tenant:     'border-plat-info-line bg-plat-info-soft text-plat-info-text',
  platform:   'border-plat-purple-line bg-plat-purple-soft text-plat-purple-text',
  permission: 'border-plat-warning-line bg-plat-warning-soft text-plat-warning-text',
};

/**
 * Actor-type badge colours. Distinct per role so a Super Admin can scan the WHO column without
 * reading it — the previous single 'chambers_user' bucket made a Chamber Admin and a Clerk
 * visually identical.
 */
const ACTOR_CHIP: Record<ActorKind, string> = {
  platform_admin: 'border-plat-purple-line bg-plat-purple-soft text-plat-purple-text',
  chamber_admin:  'border-plat-warning-line bg-plat-warning-soft text-plat-warning-text',
  barrister:      'border-plat-info-line bg-plat-info-soft text-plat-info-text',
  clerk:          'border-plat-teal-line bg-plat-teal-soft text-plat-teal-text',
  solicitor:      'border-plat-indigo-line bg-plat-indigo-soft text-plat-indigo-text',
  chambers_staff: 'border-plat-neutral-line bg-plat-neutral-soft text-plat-secondary',
  system:         'border-plat-neutral-line bg-plat-neutral-soft text-plat-muted',
};

const DATE_PRESETS = [
  { key: '24h', label: 'Last 24 hours', days: 1 },
  { key: '7d',  label: 'Last 7 days',   days: 7 },
  { key: '30d', label: 'Last 30 days',  days: 30 },
  { key: '90d', label: 'Last 90 days',  days: 90 },
] as const;

type PresetKey = (typeof DATE_PRESETS)[number]['key'];

const INPUT =
  'h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg placeholder-plat-faint ' +
  'focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50 ' +
  'disabled:cursor-not-allowed disabled:text-plat-disabled';

function fmtAbsolute(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** Last 4 characters — enough to correlate two rows, useless for enumeration. */
function shortId(id: string | null) {
  return id ? `····${id.slice(-4)}` : '—';
}

function actorName(r: ActivityRecord) {
  return r.actor.displayName ?? r.actor.email ?? 'System';
}

// ── Summary strip ───────────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }: {
  label: string; value: string | number; sub?: string; accent?: string;
}) {
  return (
    <div className="rounded-xl border border-plat-border bg-plat-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-plat-muted">{label}</div>
      <div className={`mt-1.5 text-2xl font-bold leading-none ${accent ?? 'text-plat-fg'}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-plat-muted">{sub}</div>}
    </div>
  );
}

function SummaryStrip() {
  // Refreshed on its own 30s cadence, as the old Security page did. The stream below is NOT
  // auto-refreshed: re-running a three-way union every 30s is real load for no investigative gain.
  const { data } = useQuery({
    queryKey: ['platform', 'activity', 'summary'],
    queryFn: fetchActivitySummary,
    refetchInterval: 30_000,
  });

  if (!data) {
    return <div className="h-[92px] animate-pulse rounded-xl border border-plat-border bg-plat-surface/50" />;
  }

  return (
    <div className="space-y-4">
      {data.failureRate >= 30 && (
        <div className="flex items-center gap-3 rounded-xl border border-plat-danger-line bg-plat-danger-soft px-5 py-3">
          <Warning className="h-5 w-5 shrink-0 text-plat-danger-text" weight="fill" />
          <span className="text-sm font-medium text-plat-danger-text">
            High failed sign-in rate — {data.failureRate}% of attempts in the last 24 hours failed.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Signed in (24h)" value={data.successfulLogins24h.toLocaleString()} sub="chambers members" accent="text-plat-success-text" />
        <StatCard label="Failed (24h)" value={data.failedLogins24h.toLocaleString()} sub="sign-in failures"
          accent={data.failedLogins24h > 10 ? 'text-plat-danger-text' : 'text-plat-fg'} />
        <StatCard label="Failure rate" value={`${data.failureRate}%`} sub="of all attempts"
          accent={data.failureRate >= 30 ? 'text-plat-danger-text' : data.failureRate >= 10 ? 'text-plat-warning-text' : 'text-plat-fg'} />
        {/* Two populations, reported separately. Summing them is what hid the platform
            sessions on the old page. */}
        <StatCard label="Chambers sessions" value={data.sessions.chambers.toLocaleString()} sub="active now" />
        <StatCard label="Platform sessions" value={data.sessions.platform.toLocaleString()} sub="console admins" />
      </div>
    </div>
  );
}

// ── Details drawer ──────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3 py-2">
      <dt className="text-xs font-medium text-plat-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-plat-neutral-text">{children}</dd>
    </div>
  );
}

function DetailsDrawer({ record, onClose }: { record: ActivityRecord; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Activity detail">
      <button className="absolute inset-0 bg-plat-scrim/60" aria-label="Close" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-plat-border bg-plat-elevated shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-plat-border px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[record.tone]}`} />
              <h2 className="truncate text-base font-semibold text-plat-fg">{record.label}</h2>
            </div>
            <p className="mt-0.5 text-xs text-plat-muted">{fmtAbsolute(record.occurredAt)} UTC</p>
          </div>
          <button onClick={onClose} className="shrink-0 rounded p-1 text-plat-muted hover:bg-plat-hover hover:text-plat-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        <dl className="divide-y divide-plat-border/60 px-5 py-2">
          <Field label="Actor">
            <div>{actorName(record)}</div>
            {record.actor.email && record.actor.email !== record.actor.displayName && (
              <div className="text-xs text-plat-muted">{record.actor.email}</div>
            )}
            <span className={`mt-1 inline-flex rounded border px-1.5 py-0.5 text-[11px] ${ACTOR_CHIP[record.actor.kind] ?? ACTOR_CHIP.system}`}>
              {ACTOR_KIND_LABEL[record.actor.kind] ?? record.actor.kind}
            </span>
          </Field>

          <Field label="Organisation">
            {record.organisation
              ? record.organisation.name
              : <span className="text-plat-muted">Platform level — not tenant-scoped</span>}
          </Field>

          <Field label="Target">
            {record.target ? (
              <>
                <div>
                  <span className="text-plat-secondary">{record.target.type}</span>{' '}
                  <span className="font-mono text-xs">{shortId(record.target.id)}</span>
                </div>
                {record.target.redacted && (
                  // Stated, not silently blank — an empty field reads as a bug.
                  <div className="mt-1 flex items-start gap-1.5 rounded border border-plat-warning-line bg-plat-warning-soft px-2 py-1.5 text-xs text-plat-warning-text">
                    <Lock className="mt-0.5 h-3 w-3 shrink-0" weight="fill" />
                    <span>Withheld — chambers content is not exposed to the platform console.</span>
                  </div>
                )}
                {!record.target.redacted && record.target.label && (
                  <div className="text-xs text-plat-muted">{record.target.label}</div>
                )}
              </>
            ) : <span className="text-plat-muted">—</span>}
          </Field>

          <Field label="Source">
            <span className={`inline-flex rounded border px-1.5 py-0.5 text-[11px] ${SOURCE_CHIP[record.source]}`}>
              {ACTIVITY_SOURCE_LABEL[record.source]}
            </span>
          </Field>

          <Field label="Integrity">
            {record.integrity.chained ? (
              <span className="inline-flex items-center gap-1.5 text-plat-success-text">
                <ShieldCheck className="h-4 w-4" weight="fill" />
                Chained · sequence #{record.integrity.sequenceNumber}
              </span>
            ) : (
              // platform_audit_log and permission_audit_logs have no hash chain. Saying so
              // keeps the gap visible instead of implying a guarantee that is not there.
              <span className="text-plat-muted">Not chained</span>
            )}
          </Field>

          <Field label="IP">{record.ip ?? <span className="text-plat-faint">Not recorded</span>}</Field>
          <Field label="User agent">
            {record.userAgent
              ? <span className="text-xs text-plat-secondary">{record.userAgent}</span>
              : <span className="text-plat-faint">Not recorded</span>}
          </Field>

          {record.summary && <Field label="Details">{record.summary}</Field>}

          {/* Platform rows only — a tenant record never carries `detail`. */}
          {record.detail && Object.keys(record.detail).length > 0 && (
            <div className="py-3">
              <div className="mb-1.5 text-xs font-medium text-plat-muted">Metadata</div>
              <pre className="max-h-56 overflow-auto rounded border border-plat-border bg-plat-surface p-3 text-xs text-plat-secondary">
                {JSON.stringify(record.detail, null, 2)}
              </pre>
            </div>
          )}

          <Field label="Event code">
            <span className="font-mono text-xs text-plat-muted">{record.action}</span>
          </Field>
        </dl>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────────────

export default function ActivityLogsPage() {
  // Seeded from the query string so the /platform/security and /platform/audit redirects land
  // on a meaningful view rather than a generic stream.
  const [params] = useSearchParams();

  const [category, setCategory] = useState<ActivityCategory | ''>(
    (params.get('category') as ActivityCategory | null) ?? '',
  );
  const [source, setSource] = useState<ActivitySource | ''>(
    (params.get('source') as ActivitySource | null) ?? '',
  );
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [actor, setActor] = useState('');
  const [actorType, setActorType] = useState<ActorKind | ''>('');
  const [organisationId, setOrganisationId] = useState('');
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [selected, setSelected] = useState<ActivityRecord | null>(null);
  const [downloading, setDownloading] = useState(false);

  const days = DATE_PRESETS.find((p) => p.key === preset)!.days;

  /**
   * The window start, held stable for as long as the preset is.
   *
   * This was `new Date(Date.now() - days * 86_400_000).toISOString()` evaluated inline on every
   * render. `from` is part of the React Query key below, so a millisecond-precision value meant
   * the key changed on EVERY re-render and the whole activity query refetched — triggered by any
   * unrelated state change, including switching the theme. Quantising to the minute also lets two
   * renders inside the same minute share one cache entry.
   */
  const from = useMemo(
    () => new Date(Math.floor((Date.now() - days * 86_400_000) / 60_000) * 60_000).toISOString(),
    [days],
  );

  const filters: ActivityFilters = {
    from,
    ...(source && { source }),
    ...(category && { category }),
    ...(actor.trim() && { actor: actor.trim() }),
    ...(actorType && { actorType }),
    ...(organisationId && { organisationId }),
  };

  const cursor = cursorStack[cursorStack.length - 1] ?? null;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['platform', 'activity', filters, cursor],
    queryFn: () => fetchActivity(filters, { cursor }),
    // Keeps the current rows on screen while the next query runs, instead of blanking the
    // table on every filter change.
    placeholderData: keepPreviousData,
  });

  function resetPaging() {
    setCursorStack([null]);
  }

  function clearFilters() {
    setCategory(''); setSource(''); setActor(''); setActorType(''); setOrganisationId(''); setPreset('30d');
    resetPaging();
  }

  const hasFilters = !!(category || source || actor.trim() || actorType || organisationId || preset !== '30d');

  async function handleExport() {
    setDownloading(true);
    try {
      const rows = await downloadActivityCsv(filters);
      toast.success(`Exported ${rows.toLocaleString()} rows.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setDownloading(false);
    }
  }

  const records = data?.records ?? [];

  return (
    <div className="space-y-5">
      <p className="text-sm text-plat-muted">
        Activity across the whole platform — chambers members, solicitors and platform admins.
      </p>

      <SummaryStrip />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-plat-border bg-plat-surface/60 p-3">
        <FunnelSimple className="h-4 w-4 shrink-0 text-plat-muted" />

        <select
          value={preset}
          onChange={(e) => { setPreset(e.target.value as PresetKey); resetPaging(); }}
          className={INPUT}
          aria-label="Date range"
        >
          {DATE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>

        <select
          value={source}
          onChange={(e) => { setSource(e.target.value as ActivitySource | ''); resetPaging(); }}
          className={INPUT}
          aria-label="Source"
        >
          <option value="">All sources</option>
          {ACTIVITY_SOURCES.map((s) => <option key={s} value={s}>{ACTIVITY_SOURCE_LABEL[s]}</option>)}
        </select>

        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value as ActivityCategory | ''); resetPaging(); }}
          className={INPUT}
          aria-label="Category"
        >
          <option value="">All activity</option>
          {ACTIVITY_CATEGORIES.map((c) => <option key={c} value={c}>{ACTIVITY_CATEGORY_LABEL[c]}</option>)}
        </select>

        {/* WHO acted, by authoritative role — the dimension that makes this whole-platform
            rather than platform-admin-only. */}
        <select
          value={actorType}
          onChange={(e) => { setActorType(e.target.value as ActorKind | ''); resetPaging(); }}
          className={INPUT}
          aria-label="Actor type"
        >
          <option value="">All people</option>
          {ACTOR_KINDS.map((k) => <option key={k} value={k}>{ACTOR_KIND_LABEL[k]}</option>)}
        </select>

        <input
          value={actor}
          onChange={(e) => { setActor(e.target.value); resetPaging(); }}
          placeholder="Actor email…"
          className={INPUT}
          aria-label="Actor email"
        />

        <div className="w-52">
          <TenantSelect
            value={organisationId}
            onChange={(v) => { setOrganisationId(v); resetPaging(); }}
            includeAll
            allLabel="All organisations"
          />
        </div>

        {hasFilters && (
          <button onClick={clearFilters} className="text-xs font-medium text-plat-info-text hover:underline">
            Clear filters
          </button>
        )}

        <button
          onClick={handleExport}
          disabled={downloading || records.length === 0}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-plat-border bg-plat-surface px-3 py-2 text-xs font-medium text-plat-secondary transition hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
        >
          {downloading ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <DownloadSimple className="h-3.5 w-3.5" />}
          Export CSV
        </button>
      </div>

      {data?.window.clamped && (
        <p className="text-xs text-plat-warning-text">
          Range narrowed to the maximum 90 days. Use the export for a longer investigation.
        </p>
      )}

      {/* Stream */}
      <div className="rounded-xl border border-plat-border bg-plat-surface">
        <div className="flex items-center gap-2 border-b border-plat-border px-5 py-3">
          <h2 className="text-sm font-semibold text-plat-fg">
            {hasFilters ? 'Filtered activity' : 'All activity'}
            {data && (
              <span className="ml-1.5 font-normal tabular-nums text-plat-muted">
                ({data.countMode === 'atLeast' ? `${data.total.toLocaleString()}+` : data.total.toLocaleString()})
              </span>
            )}
          </h2>
          {isFetching && !isLoading && <CircleNotch className="h-3.5 w-3.5 animate-spin text-plat-faint" />}
        </div>

        {error ? (
          <div className="px-5 py-10 text-center text-sm text-plat-danger-text">{getPlatformApiErrorMessage(error)}</div>
        ) : isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-plat-muted">
            <CircleNotch className="h-5 w-5 animate-spin" /> Loading activity…
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <Tray className="mb-2 h-8 w-8 text-plat-faint" />
            <p className="text-sm text-plat-secondary">No activity matches these filters</p>
            <p className="mt-1 text-xs text-plat-faint">Try a wider date range or clear the filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" aria-busy={isFetching}>
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  {['Event', 'Actor', 'Organisation', 'Target', 'When', ''].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-plat-muted">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {records.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelected(r);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Activity: ${r.label} by ${actorName(r)}`}
                    aria-current={selected?.id === r.id ? 'true' : undefined}
                    className={`cursor-pointer transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-plat-focus ${
                      selected?.id === r.id ? 'bg-plat-selected' : 'hover:bg-plat-hover'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[r.tone]}`} />
                        <span className="font-medium text-plat-fg">{r.label}</span>
                      </div>
                      <span className={`mt-1 inline-flex rounded border px-1.5 py-0.5 text-[10px] ${SOURCE_CHIP[r.source]}`}>
                        {ACTIVITY_SOURCE_LABEL[r.source]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-plat-secondary">{actorName(r)}</div>
                      <span className={`mt-0.5 inline-flex rounded border px-1.5 py-0.5 text-[10px] ${ACTOR_CHIP[r.actor.kind] ?? ACTOR_CHIP.system}`}>
                        {ACTOR_KIND_LABEL[r.actor.kind] ?? r.actor.kind}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-plat-secondary">
                      {r.organisation?.name ?? <span className="text-plat-faint">Platform</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.target ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-xs text-plat-muted">{r.target.type}</span>
                          <span className="font-mono text-xs text-plat-secondary">{shortId(r.target.id)}</span>
                          {r.target.redacted && <Lock className="h-3 w-3 text-plat-warning-text" weight="fill" />}
                        </span>
                      ) : <span className="text-plat-faint">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs tabular-nums text-plat-muted">
                      {fmtTime(r.occurredAt)}
                    </td>
                    <td className="px-4 py-3 text-plat-faint"><CaretRight className="h-3.5 w-3.5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Keyset paging — no page numbers, because a union has no cheap total to number against. */}
      {(cursorStack.length > 1 || data?.hasMore) && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-xs text-plat-faint">Page {cursorStack.length}</span>
          <div className="flex gap-2">
            <button
              disabled={cursorStack.length <= 1}
              onClick={() => setCursorStack((s) => s.slice(0, -1))}
              className="flex items-center gap-1 rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              <CaretLeft className="h-3 w-3" /> Previous
            </button>
            <button
              disabled={!data?.hasMore}
              onClick={() => data?.nextCursor && setCursorStack((s) => [...s, data.nextCursor])}
              className="flex items-center gap-1 rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              Next <CaretRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {selected && <DetailsDrawer record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
